import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { useBlogPosts, useBlogCategories } from '../../../hooks/useBlog';
import { 
  deleteBlogPost, 
  publishBlogPost, 
  unpublishBlogPost, 
  getBlogPosts 
} from '../../../actions';
import { Button } from '../../common/FormItems';
import { withTranslation, withUserPreferences } from '../../hoc';
import { Eye, Pencil, Trash, Clock, CheckCircle } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';

const Wrapper = styled.div`
  width: 95%;
  margin: 1.5% auto;
  padding: 0 80px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--admincat-color-grey-3);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--admincat-color-grey-2);
  font-weight: 600;
  margin: 0;
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, 
    ${props => props.gradient1 || '#4C81BE'} 0%, 
    ${props => props.gradient2 || '#3F729B'} 100%);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
  background: var(--admincat-color-grey-1);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--admincat-color-grey-3);
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fff;
  color: var(--admincat-color-grey-2);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4C81BE;
    box-shadow: 0 0 0 3px rgba(76, 129, 190, 0.1);
  }

  &::placeholder {
    color: var(--admincat-color-grey-4);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid var(--admincat-color-grey-3);
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fff;
  color: var(--admincat-color-grey-2);
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: #4C81BE;
    box-shadow: 0 0 0 3px rgba(76, 129, 190, 0.1);
  }
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #4C81BE 0%, #3F729B 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(76, 129, 190, 0.3);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 129, 190, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 220px;
  background: ${props => props.image 
    ? `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3)), url(${props.image})` 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.5));
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BlogTitle = styled.h2`
  font-size: 1.4rem;
  color: var(--admincat-color-grey-2);
  margin: 0 0 0.75rem 0;
  font-weight: 600;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    color: #4C81BE;
  }
`;

const BlogExcerpt = styled.p`
  color: var(--admincat-color-grey-3);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--admincat-color-grey-3);
  margin-top: auto;
`;

const MetaInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--admincat-color-grey-4);
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: ${props => {
    switch(props.status) {
      case 'published': return 'linear-gradient(135deg, #28a745 0%, #20c997 100%)';
      case 'draft': return 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)';
      default: return 'linear-gradient(135deg, #4C81BE 0%, #3F729B 100%)';
    }
  }};
  color: #fff;
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${props => props.danger 
    ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' 
    : props.success
    ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
    : 'linear-gradient(135deg, #4C81BE 0%, #3F729B 100%)'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: var(--admincat-color-grey-1);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h3 {
    font-size: 1.5rem;
    color: var(--admincat-color-grey-3);
    margin-bottom: 1rem;
  }

  p {
    color: var(--admincat-color-grey-3);
    margin-bottom: 2rem;
  }
`;

const LoadingState = styled(EmptyState)`
  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid var(--admincat-color-grey-3);
    border-top-color: #4C81BE;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const BlogList = ({ tr }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { categories } = useBlogCategories();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pass filters directly to the hook
  const filters = {};
  if (searchTerm) filters.search = searchTerm;
  if (selectedCategory) filters.category = selectedCategory;
  if (statusFilter) filters.status = statusFilter;

  const { posts, isLoading } = useBlogPosts(filters);

  const handleDelete = async (post, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: tr('Are you sure?'),
      text: tr('This blog post will be permanently deleted'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteBlogPost(post.slug));
        // Refetch with current filters after delete
        dispatch(getBlogPosts(filters));
        Swal.fire(tr('Success'), tr('Blog post deleted successfully'), 'success');
      } catch (error) {
        Swal.fire(tr('Error'), tr('Failed to delete blog post'), 'error');
      }
    }
  };

  const handlePublish = async (post, e) => {
    e.stopPropagation();
    try {
      if (post.status === 'published') {
        await dispatch(unpublishBlogPost(post.slug));
        Swal.fire(tr('Success'), tr('Blog post unpublished'), 'success');
      } else {
        await dispatch(publishBlogPost(post.slug));
        Swal.fire(tr('Success'), tr('Blog post published'), 'success');
      }
      // Refetch with current filters after publish/unpublish
      dispatch(getBlogPosts(filters));
    } catch (error) {
      Swal.fire(tr('Error'), tr('Failed to update post status'), 'error');
    }
  };

  const postsData = posts?.results || posts || [];
  const categoriesData = categories?.results || categories || [];

  const stats = {
    total: postsData.length,
    published: postsData.filter(p => p.status === 'published').length,
    draft: postsData.filter(p => p.status === 'draft').length,
  };

  if (isLoading) {
    return (
      <Wrapper>
        <LoadingState>
          <div className="spinner" />
          <h3>{tr('Loading posts...')}</h3>
        </LoadingState>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Title>{tr('Blog Management')}</Title>
        <CreateButton onClick={() => navigate('/admin/dashboard/blog/posts/new')}>
          <span>+</span> {tr('Create New Post')}
        </CreateButton>
      </Header>

      <StatsBar>
        <StatCard gradient1="#4C81BE" gradient2="#3F729B">
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>{tr('Total Posts')}</StatLabel>
        </StatCard>
        <StatCard gradient1="#28a745" gradient2="#20c997">
          <StatNumber>{stats.published}</StatNumber>
          <StatLabel>{tr('Published')}</StatLabel>
        </StatCard>
        <StatCard gradient1="#ffc107" gradient2="#ff9800">
          <StatNumber>{stats.draft}</StatNumber>
          <StatLabel>{tr('Drafts')}</StatLabel>
        </StatCard>
      </StatsBar>

      <FilterBar>
        <SearchInput
          type="text"
          placeholder={tr('Search posts by title or content...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">{tr('All Categories')}</option>
          {categoriesData.map(cat => (
            <option key={cat.id} value={cat.slug}>{cat.name}</option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{tr('All Status')}</option>
          <option value="published">{tr('Published')}</option>
          <option value="draft">{tr('Draft')}</option>
        </FilterSelect>
      </FilterBar>

      {postsData.length === 0 ? (
        <EmptyState>
          <h3>{tr('No posts found')}</h3>
          <p>{tr('Start creating amazing content for your audience')}</p>
          <CreateButton onClick={() => navigate('/admin/dashboard/blog/posts/new')}>
            <span>+</span> {tr('Create Your First Post')}
          </CreateButton>
        </EmptyState>
      ) : (
        <BlogGrid>
          {postsData.map((post) => (
            <BlogCard key={post.id}>
              {post.is_featured && <FeaturedBadge>⭐ {tr('Featured')}</FeaturedBadge>}
              <ImageWrapper 
                image={post.featured_image}
                // onClick={() => navigate(`/admin/dashboard/blog/posts/${post.slug}/edit`)}
                onClick={() =>  navigate(`/blog/${post.slug}`)}
              />
              <CardContent>
                <BlogTitle 
                  // onClick={() => navigate(`/admin/dashboard/blog/posts/${post.slug}/edit`)}
                  onClick={() =>  navigate(`/blog/${post.slug}`)}
                >
                  {post.title}
                </BlogTitle>
                <BlogExcerpt>{post.excerpt || tr('No excerpt available')}</BlogExcerpt>
                <CardFooter>
                  <MetaInfo>
                    <CategoryBadge status={post.status}>
                      {post.status === 'published' ? <CheckCircle /> : <Clock />}
                      {tr(post.status)}
                    </CategoryBadge>
                    <MetaRow>
                      {post.category?.name || tr('Uncategorized')} • {post.views_count || 0} {tr('views')}
                    </MetaRow>
                  </MetaInfo>
                  <ActionButtons>
                    <ActionButton onClick={(e) => { e.stopPropagation(); navigate(`/blog/${post.slug}`); }}>
                      <Eye /> {tr('View')}
                    </ActionButton>
                    <ActionButton onClick={(e) => { e.stopPropagation(); navigate(`/admin/dashboard/blog/posts/${post.slug}/edit`); }}>
                      <Pencil /> {tr('Edit')}
                    </ActionButton>
                    {/* {post.status === 'draft' && (
                      <ActionButton success onClick={(e) => handlePublish(post, e)}>
                        <CheckCircle /> {tr('Publish')}
                      </ActionButton>
                    )} */}
                      {post.status === 'draft' ? (
                      <ActionButton success onClick={(e) => handlePublish(post, e)}>
                        <CheckCircle /> {tr('Publish')}
                      </ActionButton>
                    ) : (
                      <ActionButton onClick={(e) => handlePublish(post, e)}>
                        <Clock /> {tr('Unpublish')}
                      </ActionButton>
                    )}
                    <ActionButton danger onClick={(e) => handleDelete(post, e)}>
                      <Trash /> {tr('Delete')}
                    </ActionButton>
                  </ActionButtons>
                </CardFooter>
              </CardContent>
            </BlogCard>
          ))}
        </BlogGrid>
      )}
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withUserPreferences,
)(BlogList);